import { useQuery } from '@tanstack/react-query';
import type { FunnelDetailResponse } from '../../lib/api.js';
import { formatDuration, formatNumber } from '../../lib/formatters.js';
import { CHART_COLORS } from './constants.js';

/**
 * @graph-stack ux-mirror
 * @graph-domain UX
 * @graph-business 通常ファネル用タブ。逆台形ファネルチャート + ステップ別詳細テーブル + ドロップオフウォーターフォールを描画する。
 * @graph-connects formatNumber [calls] 数値フォーマット
 * @graph-connects formatDuration [calls] 滞在時間フォーマット
 */
export function FunnelTab({
  detailQuery,
}: {
  detailQuery: ReturnType<typeof useQuery<FunnelDetailResponse>>;
}) {
  if (detailQuery.isLoading) {
    return (
      <div className="card">
        <div className="skeleton-shimmer h-80 w-full rounded-lg" />
      </div>
    );
  }

  const data = detailQuery.data;
  if (!data || data.steps.length === 0) {
    return (
      <div className="card">
        <p className="text-sm text-[var(--color-text-muted)] text-center py-8">
          データがありません
        </p>
      </div>
    );
  }

  const maxUsers = data.steps[0]?.uniqueUsers ?? 1;

  return (
    <div className="space-y-6">
      {/* 逆台形ファネルチャート */}
      <div className="card animate-fade-in-up">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
          ステップ別ユニークユーザー数
        </h3>
        <div className="flex flex-col items-center gap-0">
          {data.steps.map((step, idx) => {
            // 幅を最初のステップ（100%）からの比率で計算
            const widthPct = maxUsers > 0 ? Math.max((step.uniqueUsers / maxUsers) * 100, 8) : 8;
            const color = CHART_COLORS[idx % CHART_COLORS.length];
            const prevUsers = idx > 0 ? data.steps[idx - 1].uniqueUsers : null;
            const dropPct =
              prevUsers !== null && prevUsers > 0
                ? Math.round(((prevUsers - step.uniqueUsers) / prevUsers) * 1000) / 10
                : null;

            return (
              <div key={step.stepIndex} className="w-full flex flex-col items-center">
                {dropPct !== null && (
                  <div className="flex items-center gap-2 py-0.5">
                    <div className="h-px w-8 bg-[var(--color-border)]" />
                    <span
                      className={`text-[10px] font-medium ${
                        dropPct > 50
                          ? 'text-[var(--color-error)]'
                          : 'text-[var(--color-text-muted)]'
                      }`}
                    >
                      ▼ {String(dropPct)}% 離脱
                    </span>
                    <div className="h-px w-8 bg-[var(--color-border)]" />
                  </div>
                )}
                <div
                  className="relative flex items-center justify-center py-2.5 transition-all duration-300"
                  style={{
                    width: `${String(widthPct)}%`,
                    minWidth: '120px',
                    background: color,
                    opacity: 0.85,
                    clipPath:
                      idx < data.steps.length - 1
                        ? (() => {
                            const nextWidthPct =
                              maxUsers > 0
                                ? Math.max((data.steps[idx + 1].uniqueUsers / maxUsers) * 100, 8)
                                : 8;
                            const insetPct =
                              widthPct > 0
                                ? Math.max(((widthPct - nextWidthPct) / widthPct) * 50, 0)
                                : 0;
                            return `polygon(0 0, 100% 0, ${String(100 - insetPct)}% 100%, ${String(insetPct)}% 100%)`;
                          })()
                        : 'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
                    borderRadius:
                      idx === 0
                        ? '8px 8px 0 0'
                        : idx === data.steps.length - 1
                          ? '0 0 8px 8px'
                          : '0',
                  }}
                >
                  <div className="flex items-center gap-2 text-white text-xs font-medium z-10 px-3">
                    <span className="truncate max-w-[140px]">{step.stepName}</span>
                    <span className="font-mono font-bold">{formatNumber(step.uniqueUsers)}</span>
                    <span className="opacity-70">({String(step.conversionRate)}%)</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ステップ詳細テーブル */}
      <div className="card animate-fade-in-up">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
          ステップ別詳細
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                  #
                </th>
                <th className="text-left py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                  ステップ
                </th>
                <th className="text-left py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                  Screen / Event
                </th>
                <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                  ユニークユーザー
                </th>
                <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                  平均滞在時間
                </th>
                <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                  離脱率
                </th>
                <th className="text-right py-2 px-3 text-xs font-medium text-[var(--color-text-muted)]">
                  コンバージョン率
                </th>
              </tr>
            </thead>
            <tbody>
              {data.steps.map((step) => (
                <tr
                  key={step.stepIndex}
                  className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors"
                >
                  <td className="py-2.5 px-3 text-[var(--color-text-muted)]">
                    {String(step.stepIndex + 1)}
                  </td>
                  <td className="py-2.5 px-3 font-medium text-[var(--color-text-primary)]">
                    {step.stepName}
                  </td>
                  <td className="py-2.5 px-3 text-xs text-[var(--color-text-muted)]">
                    <span className="font-mono">{step.screen}</span>
                    {step.event !== 'screen_rendered' && (
                      <span className="ml-1 px-1.5 py-0.5 rounded bg-[var(--color-accent-surface)] text-[var(--color-accent-primary)] text-[10px]">
                        {step.event}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono text-[var(--color-text-primary)]">
                    {formatNumber(step.uniqueUsers)}
                  </td>
                  <td className="py-2.5 px-3 text-right font-mono text-[var(--color-text-secondary)]">
                    {step.avgDwellTimeMs != null ? (
                      formatDuration(step.avgDwellTimeMs)
                    ) : (
                      <span className="text-[var(--color-text-muted)]">—</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    {step.dropoffRate !== null ? (
                      <span
                        className={
                          step.dropoffRate > 50
                            ? 'text-[var(--color-error)] font-medium'
                            : 'text-[var(--color-text-secondary)]'
                        }
                      >
                        {String(step.dropoffRate)}%
                      </span>
                    ) : (
                      <span className="text-[var(--color-text-muted)]">—</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-[var(--color-bg-hover)] overflow-hidden">
                        <div
                          className="h-full rounded-full bg-[var(--color-accent-primary)]"
                          style={{ width: `${String(Math.min(step.conversionRate, 100))}%` }}
                        />
                      </div>
                      <span className="text-[var(--color-text-primary)] font-mono text-xs w-12 text-right">
                        {String(step.conversionRate)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ドロップオフウォーターフォール */}
      <div className="card animate-fade-in-up">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4">
          ステップ間ドロップオフ
        </h3>
        <div className="space-y-2">
          {data.steps.map((step, idx) => {
            if (idx === 0) return null;
            const prevUsers = data.steps[idx - 1].uniqueUsers;
            const dropped = prevUsers - step.uniqueUsers;
            const dropPct = prevUsers > 0 ? (dropped / prevUsers) * 100 : 0;
            const barWidth = Math.min(dropPct, 100);

            return (
              <div key={step.stepIndex} className="flex items-center gap-3">
                <span className="text-xs text-[var(--color-text-muted)] w-32 truncate flex-shrink-0">
                  {data.steps[idx - 1].stepName} → {step.stepName}
                </span>
                <div className="flex-1 h-5 rounded bg-[var(--color-bg-hover)] overflow-hidden relative">
                  <div
                    className="h-full rounded bg-[var(--color-error)] opacity-60"
                    style={{ width: `${String(barWidth)}%` }}
                  />
                  <span className="absolute inset-0 flex items-center px-2 text-[10px] font-medium text-[var(--color-text-primary)]">
                    -{formatNumber(dropped)} ({String(Math.round(dropPct * 10) / 10)}%)
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
